import './Teachers.scss'

import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, Input, Modal, notification, Row, Table } from 'antd'
import DeleteModal from 'components/okulyonetim-admin/delete-modal/DeleteModal'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import AppContext from '../../../../context/AppContext'
import { useOgretmenSilMutation, useOkulOgretmenleriQuery } from '../../../../gql/GeneratedGraphqlTypes'
import Spinner from '../../../spinner/Spinner'
import { ReactComponent as TeacherIcon } from './teacher.svg'

const { Search } = Input

const Teachers: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [openClassList, setOpenClassList] = useState<{ [key: string]: boolean }>({})
  const [openLessonList, setOpenLessonList] = useState<{ [key: string]: boolean }>({})
  const [submitting, setSubmitting] = useState(false)
  const [ogretmenSil] = useOgretmenSilMutation()

  const { okul } = useContext(AppContext)
  const { data, loading, refetch, error } = useOkulOgretmenleriQuery({
    variables: {
      sube_id: okul?.id,
      arama: searchValue || null,
    },
    fetchPolicy: 'network-only',
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [modalMessage, setModalMessage] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const showDeleteModal = (ids: string[]) => {
    setDeleteId(ids.join(','))
    setIsDeleteModalVisible(true)
  }

  const handleDelete = async (ids: string) => {
    setSubmitting(true)
    const idList: number[] = ids.split(',').map((id) => Number(id))

    try {
      const response = await ogretmenSil({
        variables: {
          id: idList,
        },
      })

      if (response.data?.ogretmenSil.success && !response.data.ogretmenSil.silinemeyenler?.length) {
        notification.success({
          message: '',
          description: `${response.data?.ogretmenSil?.message}`,
          duration: 6,
        })
        setIsDeleteModalVisible(false)
        setSelectedRowKeys([])
        await refetch()
      } else {
        const silinemeyenler = response.data?.ogretmenSil?.silinemeyenler
        if (silinemeyenler && silinemeyenler[0] && silinemeyenler[0].neden) {
          setModalTitle(silinemeyenler[0].neden[0] ?? 'Bir hata oluştu.')
        } else {
          setModalTitle('Bir hata oluştu.')
        }
        setIsModalOpen(true)

        // 🔹 Silinenler Listesi
        const silinenler = response.data?.ogretmenSil?.silinenler?.map((item) => `✔ Silindi: ${item?.isim}`) ?? []

        // 🔹 Silinemeyenler Listesi (Daha açıklayıcı hale getirildi)
        const silinemeyenlerList =
          silinemeyenler?.map((item) => {
            const ilgiliKayitlar = item?.ilgili_kayitlar?.map((kayit) => `🛑Sınıf Dersi: ${kayit}`).join('; ') || 'Bağlı kayıt yok'

            return `${ilgiliKayitlar}`
          }) ?? []

        // 🔹 Modal Mesajını Güncelle
        setModalMessage([...silinenler, ...silinemeyenlerList])
      }
    } catch (error) {
      notification.error({
        message: '',
        description: 'Listeyi gönderirken bir hata oluştu.',
        duration: 6,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }
  useEffect(() => {
    refetch()
  }, [searchValue, refetch])

  const handleSearch = () => {
    setSearchValue(searchTerm)
  }
  if (loading) {
    return <Spinner showSpinner={loading} color="red" />
  }

  if (error) {
    console.error(error)
    navigate('/')
    return null
  }

  if (submitting) {
    return <Spinner showSpinner={submitting} color="red" />
  }

  const handleOpenClassList = (record: any) => {
    if (record.siniflar && record.siniflar.length > 0) {
      setOpenClassList((prevState) => ({
        ...prevState,
        [record.id]: !prevState[record.id],
      }))
    }
  }

  const handleOpenLessonList = (record: any) => {
    if (record.dersler && record.dersler.length > 0) {
      setOpenLessonList((prevState) => ({
        ...prevState,
        [record.id]: !prevState[record.id],
      }))
    }
  }

  const filteredData = data?.okulOgretmenleri?.ogretmenler?.filter(Boolean) ?? []
  const totalItems = filteredData.length
  const displayedItems = selectedRowKeys.length
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
    },

    {
      title: 'Öğretmen Bilgileri',
      dataIndex: 'ogretmenBilgi',
      key: 'ogretmenBilgi',
      render: (_: any, record: any) => (
        <div className="teacher-info-column">
          <span>{record.ogretmen}</span>
          <span>{record.email}</span>
        </div>
      ),
    },
    { title: 'Telefon', dataIndex: 'cep_telefonu', key: 'cep_telefonu' },
    { title: 'Okul', dataIndex: 'okul', key: 'okul' },
    { title: 'Şube', dataIndex: 'sube', key: 'sube' },
    {
      title: 'Sınıflar',
      dataIndex: 'siniflar',
      key: 'siniflar',
      render: (_: any, record: any) => (
        <>
          <div
            onClick={() => handleOpenClassList(record)}
            style={{ cursor: record.siniflar && record.siniflar.length > 0 ? 'pointer' : '' }}
            className="teacher-columns"
          >
            <span className={record.siniflar && record.siniflar.length > 0 ? 'title' : ''}>Sınıf Listesi</span>
            <span className="record-length">{record.siniflar ? record.siniflar.length : 0}</span>
          </div>

          <Modal
              width={700}
              footer={null}
              open={openClassList[record.id]}
              onCancel={() => setOpenClassList((prevState) => ({...prevState, [record.id]: false}))}
              className="teacher-modal"
              title={
                <div className="teacher-modal-header">
                  <div className="teacher-modal-title-div">
                    <span className="title">Öğretmen</span>
                    <span>{record.ogretmen}</span>
                  </div>
                  <div className="teacher-modal-title-div">
                    <span className="title">Şube</span>
                    <span>{record.sube}</span>
                  </div>
                  <div className="teacher-modal-title-div">
                    <span className="title">Okul</span>
                    <span>{record.okul}</span>
                  </div>
                </div>
              }
          >
            <h3>Sınıflar</h3>
            <div>
              {record.siniflar && record.siniflar.length > 0 ? (
                  <ul>
                    {record.siniflar.map((sinif: any, index: number) => (
                        <li key={index}>{sinif}</li>
                    ))}
                  </ul>
              ) : (
                  <p>Herhangi bir sınıf bulunamadı.</p>
              )}
            </div>
          </Modal>
        </>
),
},
{
  title: 'Dersler',
      dataIndex
:
  'dersler',
      key: 'dersler',
      render: (_: any, record: any) => (
        <>
          <div
            onClick={() => handleOpenLessonList(record)}
            style={{ cursor: record.dersler && record.dersler.length > 0 ? 'pointer' : '' }}
            className="teacher-columns"
          >
            <span className={record.dersler && record.dersler.length > 0 ? 'title' : ''}>Ders Listesi</span>
            <span className="record-length">{record.dersler ? record.dersler.length : 0}</span>
          </div>
          <Modal
              width={700}
              footer={null}
              className="teacher-modal"
              title={
                <div className="teacher-modal-header">
                  <div className="teacher-modal-title-div">
                    <span className="title">Öğretmen</span>
                    <span>{record.ogretmen}</span>
                  </div>
                  <div className="teacher-modal-title-div">
                    <span className="title">Şube</span>
                    <span>{record.sube}</span>
                  </div>
                  <div className="teacher-modal-title-div">
                    <span className="title">Okul</span>
                    <span>{record.okul}</span>
                  </div>
                </div>
              }
              open={openLessonList[record.id]}
              onCancel={() => setOpenLessonList((prevState) => ({...prevState, [record.id]: false}))}
          >
            <h3>Dersler</h3>
            <div style={{maxHeight: '400px', overflowY: 'auto', paddingRight: '10px'}}>
              {record.dersler && record.dersler.length > 0 ? (
                  <ul>
                    {record.dersler.map((ders: string, index: number) => (
                        <li key={index}>{ders}</li>
                    ))}
                  </ul>
              ) : (
                  <p>Herhangi bir ders bulunamadı.</p>
              )}
            </div>
          </Modal>
        </>
),
},
{
  title: '',
      dataIndex
:
  'ogretmen_detay',
      key: 'ogretmen_detay',
      render: (_: any, record: any) => (
        <Button onClick={() => navigate(`${record.id}`)} className="okul-yonetim-admin-detail-button">
          Detay
        </Button>
      ),
    },
  ]

  return (
    <div className="okul-ogretmen-page">
      <div className="okul-sepeti-admin-header">
        <h1>Öğretmenler</h1>
      </div>
      <Row gutter={8} className="filter-section">
        <Col span={6}>
          <h4>Öğretmen Ara</h4>
          <Search
            placeholder="Aramak istediğiniz öğretmeni yazınız"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            allowClear
            enterButton={
              <div className="search-suffix-div">
                Ara <SearchOutlined />{' '}
              </div>
            }
          />
        </Col>
        <Col className="button-right">
          <Button icon={<TeacherIcon />} onClick={() => navigate('/okulyonetimadmin/ogretmen-ekle')}>
            Öğretmen Ekle
          </Button>
        </Col>
      </Row>

      <Table
        dataSource={filteredData as object[]}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          total: totalItems,
          showTotal: (total) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                style={{
                  display: selectedRowKeys.length === 0 ? 'none' : '',
                }}
                icon={<DeleteOutlined />}
                onClick={() => showDeleteModal(selectedRowKeys.map((id) => id.toString()))}
                type="primary"
                danger
              >
                Seçilen {selectedRowKeys.length} Sınıfları Sil
              </Button>

              <span style={{ marginLeft: '10px' }}>
                {' '}
                {/* Buton ile metin arasına boşluk koyuyoruz */}
                Seçilen: {displayedItems} / Toplam: {total}
              </span>
            </div>
          ),
          onChange: (page, size) => {
            setCurrentPage(page)
            setPageSize(size)
          },
        }}
        loading={loading}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
        rowKey="id"
        className="okul-yonetim-admin-table"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
          ...Selection,
        }}
      />
      <DeleteModal
        title="Seçilen Öğretmenleri Sil"
        id={deleteId || ''}
        visible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onDelete={handleDelete}
      />
      <Modal
          onCancel={handleCancel}
          footer={<Button onClick={handleCancel}>Kapat</Button>}
          title={modalTitle}
          open={isModalOpen}
      >
        <ul>
          {modalMessage.flatMap((message, index) =>
              message
                  .split(';')
                  .map((item, subIndex) => (
                      <li key={`${index}-${subIndex}`}>
                        {item.trim().replace(/^\{?Sınıf Dersi=/, '').replace(/\}?$/, '')}
                      </li>
                  )),
          )}
        </ul>
      </Modal>
    </div>
  )
}

export default Teachers
